import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragMove,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Observable, Subscription } from 'rxjs';
import { EmpleadoService } from 'src/app/empleado/empleado.service';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Empleado } from 'src/app/shared/model/empleado/empleado.model';
import { Puesto } from 'src/app/shared/model/empleado/puesto.model';
import { Persona } from 'src/app/shared/model/security/persona.model';
import { Usuario } from 'src/app/shared/model/security/usuario.model';

const speed = 10;

/**
 * Asignación de empleados arrastrándolos entre "Recursos Disponibles" y "Recursos Asignados".
 * La usan el equipo de un proyecto (administración de proyectos y seguimiento de oportunidades ODP)
 * y los equipos de desarrollo y de QA de una etapa; cada componente conserva su selector y su hoja de estilos.
 * Administración de proyectos y los equipos de la etapa pintan la misma plantilla,
 * proyectos/admin-proyectos/modal-agregar-proyecto/agregar-empleados-proyecto/add-emp-proy.component.html.
 * El seguimiento ODP conserva la suya: ahí el buscador, los botones y los disponibles van dentro de *ngIf, y en
 * las otras tres son elementos fijos que se pintan aunque el primer ciclo de detección se interrumpa (p. ej. si
 * ngOnInit truena con un recurso sin puesto).
 */
@Directive()
export abstract class AgregarEmpleadosBase implements OnInit {
  resourcesDataDevelopers: Array<Empleado>;
  resourcesFilterDevelopers: Array<Empleado>;
  doneDevelopers: Array<Empleado>;
  requestEmpleados = new ConsultaList<Empleado>();
  @Output() emmitResourcesDevelopers = new EventEmitter<Empleado[]>();
  @Input() recursosAsignadosDevelopers: Empleado[];
  @ViewChild('scrollElementContentDevelopers')
  scrollElementContentDevelopers: ElementRef<HTMLElement>;
  @ViewChildren(CdkDrag)
  dragEls: QueryList<CdkDrag>;
  private animationFrame: number | undefined;
  subs = new Subscription();

  /**
   * Área de los puestos que se asignan en una etapa: 1 desarrollo, 2 QA. El equipo de un proyecto no
   * tiene área: lista a cada empleado activo, pinta el buscador con contorno y sin marcarlo como
   * requerido, y al quedar vacía la lista de asignados tras un arrastre no restablece la de disponibles.
   */
  protected readonly idArea: number | null = null;

  protected readonly empleadosService = inject(EmpleadoService);

  /** Va como entrada y como atributo: el equipo de proyecto llevaba appearance="outline" escrito en la etiqueta. */
  get aparienciaBuscador(): MatFormFieldAppearance | null {
    return this.idArea === null ? 'outline' : null;
  }

  /**
   * La plantilla trae además el atributo required fijo, como la de los equipos de la etapa: así su buscador lo
   * tiene desde que se crea, aunque el primer ciclo de detección se interrumpa. En el equipo de proyecto, que no
   * lo llevaba, este enlace (entrada de matInput) y atributoRequerido se lo quitan en el primer ciclo.
   */
  get buscadorRequerido(): boolean {
    return this.idArea !== null;
  }

  /**
   * Quita el atributo required fijo al actualizar la plantilla, antes de pintar los renglones: si uno truena
   * (p. ej. un colaborador sin persona), el buscador del equipo de proyecto queda sin required, como antes.
   */
  get atributoRequerido(): '' | null {
    return this.buscadorRequerido ? '' : null;
  }

  /**
   * Recursos Asignados de entrada. En la etapa es una COPIA filtrada por área: quien no tiene puesto o es de
   * otra área no se pinta aquí y la etapa lo conserva por su cuenta (construyeRecursosEtapa). En el equipo
   * de proyecto (sin área) es el mismo arreglo que manda el padre.
   */
  ngOnInit() {
    this.doneDevelopers = new Array();
    if (this.recursosAsignadosDevelopers && this.recursosAsignadosDevelopers.length > 0) {
      const recursos = this.idArea === null
        ? this.recursosAsignadosDevelopers
        : this.recursosAsignadosDevelopers.filter(item => item?.puesto?.id_area === this.idArea);
      if (recursos) {
        this.doneDevelopers = recursos;
        this.emiteRecursos();
      }
    }
    this.requestEmpleados = new ConsultaList<Empleado>();
    this.requestEmpleados.regXPagina = 1000;
    this.requestEmpleados.regInicial = 0;
    this.requestEmpleados.bean = new Empleado();
    this.requestEmpleados.bean.usuario = new Usuario();
    this.requestEmpleados.bean.persona = new Persona();
    this.requestEmpleados.bean.puesto = new Puesto();
    this.consultarEmpleados();
  }

  /**
   * resourcesDataDevelopers es la lista completa del servidor y no se toca; los disponibles son otro arreglo
   * (la lista menos los asignados). Antes eran el mismo: quitar a los asignados de los disponibles los quitaba
   * también de la lista completa, y "Agregar a todos" dejaba fuera justo a los que ya estaban asignados.
   */
  consultarEmpleados(): void {
    this.empleadosDisponibles().subscribe(responseEmpleadosDisponibles => {
      if (responseEmpleadosDisponibles.list.length !== 0) {
        this.resourcesDataDevelopers = responseEmpleadosDisponibles.list;
        this.resourcesFilterDevelopers = this.sinAsignados(this.resourcesDataDevelopers);
      }
    });
  }

  /** Los de la lista que no están entre los asignados (por idEmpleado: los asignados son otra copia). */
  private sinAsignados(lista: Array<Empleado>): Array<Empleado> {
    const asignados = new Set(
      (this.doneDevelopers ?? []).map(item => item?.idEmpleado).filter(id => id != null)
    );
    return lista.filter(item => !asignados.has(item?.idEmpleado));
  }

  private empleadosDisponibles(): Observable<ConsultaList<Empleado>> {
    if (this.idArea === null) {
      return this.empleadosService.consultaEmpleadosActivos();
    }
    this.requestEmpleados.bean.puesto.id = null;
    this.requestEmpleados.bean.puesto.id_area = this.idArea;
    return this.empleadosService.consultaEmpleadosPorPuesto(this.requestEmpleados);
  }

  dropDevelopers(event: CdkDragDrop<Array<Empleado>>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.emiteRecursos();
    if (this.idArea !== null && this.doneDevelopers?.length === 0 && this.resourcesDataDevelopers) {
      this.resourcesFilterDevelopers = [...this.resourcesDataDevelopers];
    }
  }

  /** Suma a los asignados todos los disponibles; los que ya estaban asignados se quedan. */
  addAllResourcesDevelopers() {
    if (!this.resourcesDataDevelopers) {
      return;
    }
    this.doneDevelopers = [...(this.doneDevelopers ?? []), ...this.sinAsignados(this.resourcesDataDevelopers)];
    this.resourcesFilterDevelopers = new Array();
    this.emiteRecursos();
  }
  removeAllResourcesDevelopers() {
    this.doneDevelopers = new Array();
    this.resourcesFilterDevelopers = [...(this.resourcesDataDevelopers ?? [])];
    this.emiteRecursos();
  }

  applyFilterDevelopers(filterValue: string) {
    if (!this.resourcesDataDevelopers) {
      return;
    }
    const buscado = (filterValue ?? '').trim().toLowerCase();
    this.resourcesFilterDevelopers = this.resourcesDataDevelopers.filter(
      value => (value.persona?.nombreCompleto ?? '').toLowerCase().includes(buscado)
    );
    this.removeAsignados();
  }

  /** Quita de los disponibles a los ya asignados, por idEmpleado (los asignados pueden ser otra copia). */
  removeAsignados() {
    this.resourcesFilterDevelopers = this.sinAsignados(this.resourcesFilterDevelopers ?? []);
  }

  emiteRecursos() {
    this.emmitResourcesDevelopers.emit(this.doneDevelopers);
  }

  @bound
  public triggerScroll($event: CdkDragMove) {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
    this.animationFrame = requestAnimationFrame(() => this.scroll($event));
  }

  private scroll($event: CdkDragMove) {
    const { y } = $event.pointerPosition;
    const baseEl = this.scrollElementContentDevelopers.nativeElement;
    const box = baseEl.getBoundingClientRect();
    const scrollTop = baseEl.scrollTop;
    const top = box.top + -y;
    if (top > 0 && scrollTop !== 0) {
      const newScroll = scrollTop - speed * Math.exp(top / 50);
      baseEl.scrollTop = newScroll;
      this.animationFrame = requestAnimationFrame(() => this.scroll($event));
      return;
    }

    const bottom = y - box.bottom;
    if (bottom > 0 && scrollTop < box.bottom) {
      const newScroll = scrollTop + speed * Math.exp(bottom / 50);
      baseEl.scrollTop = newScroll;
      this.animationFrame = requestAnimationFrame(() => this.scroll($event));
    }
  }
}

/** Deja el método atado a su instancia la primera vez que se lee (o a la clase, si es estático). */
export function bound(target: object, propKey: string | symbol): PropertyDescriptor {
  const originalMethod: unknown = Reflect.get(target, propKey);
  if (typeof originalMethod !== 'function') {
    throw new TypeError('@bound can only be used on methods.');
  }

  if (typeof target === 'function') {
    return {
      value: (...args: unknown[]) => originalMethod.apply(target, args)
    };
  }
  return {
    get(this: object) {
      const atado = (...args: unknown[]) => originalMethod.apply(this, args);
      Object.defineProperty(this, propKey.toString(), { value: atado });
      return atado;
    }
  };
}
